import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { ApplicationState } from '../store';
import * as SubFormState from '../store/SubForm';
import {
    Trade,
    SubmitForm,
    SubFormVM,
    FormAttachment,
    HeldLicense,
} from '../store/interface';
import { TradeListSelection } from './SubComponent/TradeListSelection';
import ServiceAreas from './SubComponent/ServiceAreas';
import * as download from 'downloadjs';
import { countriesShort, states, serviceAreas } from './constants';
import { SliderCheckbox } from './SubComponent/SliderCheckbox';
import { F40FormGroup, F40FormRow } from './f40form/F40Form';
import '../css/f40form.css';
import { useDispatch } from 'react-redux';
import { Modal2 } from './SubComponent/Modal';
import { validateForm, FormValidationResult } from './formValidation';
import {
    VENDOR_TYPES,
    FEDERAL_CERTIFICATIONS,
    requiresTradesAndLicenses,
    requiresSubAcknowledgment,
    DEFAULT_COUNTRY,
} from './formConstants';

export interface FormState {
    readonly vendorName: string;
    readonly state: string;
    readonly city: string;
    readonly zip: string;
    readonly billingAddress: string;

    readonly p_state: string;
    readonly p_city: string;
    readonly p_zip: string;
    readonly p_Address: string;

    readonly distributedEnergyMarket: boolean;
    readonly evMarket: boolean;
    readonly retailMarket: boolean;
    readonly evitpCertified: boolean;

    readonly contactName: string;
    readonly contactEmail: string;
    readonly contactTitle: string;
    readonly businessPhone: string;
    readonly alternativePhone: string;
    readonly referral: string;
    readonly notes: string;
    readonly union: string;
    readonly country: string;
    readonly p_country: string;
    readonly vendorType: string;
    readonly federalCertification: string[];
    readonly passed: boolean;
    readonly trades: number;
    readonly bparr1: string;
    readonly bparr2: string;
    readonly bparr3: string;
    readonly zipOrexe: boolean;
    readonly sameAddress: boolean;
    readonly heldLicenses: HeldLicense[];
    readonly yearsInBusiness: number;
    readonly numberOfEmployees: number;
    readonly modRate3Years: string[];
    readonly hasSafetyPolicy: boolean;
    readonly vendorId: number;
    readonly w9Form: string;
    readonly evidenceOfCoverage: string;
    readonly sBEForm: string;
    readonly mBEForm: string;
    readonly wBEForm: string;
    readonly vBEForm: string;
    readonly uSBLNForm: string;
    readonly hZBEForm: string;
    readonly dBEForm: string;
    readonly dVBEForm: string;
    readonly lGBTBEForm: string;
    readonly isSubAcknowledged: boolean;
    readonly attachments: FormAttachment[];
    readonly website?: string;
    readonly certificateOfInsurance: string;
    readonly bankingInfo: string;
    readonly licenseFiles: (File | null)[];
}

const emptyForm: FormState = {
    vendorName: '',
    state: '',
    city: '',
    zip: '',
    billingAddress: '',

    p_state: '',
    p_city: '',
    p_zip: '',
    p_Address: '',
    distributedEnergyMarket: false,
    evMarket: false,
    retailMarket: false,
    evitpCertified: false,

    contactName: '',
    contactEmail: '',
    contactTitle: '',
    businessPhone: '',
    alternativePhone: '',
    referral: '',
    notes: '',
    union: '',
    country: DEFAULT_COUNTRY,
    p_country: DEFAULT_COUNTRY,
    federalCertification: [],
    heldLicenses: [] as HeldLicense[],
    vendorType: '',
    website: '',
    passed: true,
    trades: 0,
    bparr1: '',
    bparr2: '',
    bparr3: '',
    zipOrexe: false,
    sameAddress: false,

    yearsInBusiness: 0,
    numberOfEmployees: 0,
    modRate3Years: ['0.0', '0.0', '0.0'],
    hasSafetyPolicy: false,
    vendorId: 0,
    w9Form: '',
    evidenceOfCoverage: '',
    sBEForm: '',
    mBEForm: '',
    wBEForm: '',
    vBEForm: '',
    uSBLNForm: '',
    hZBEForm: '',
    dBEForm: '',
    dVBEForm: '',
    lGBTBEForm: '',
    isSubAcknowledged: false,
    attachments: [],
    certificateOfInsurance: '',
    bankingInfo: '',
    licenseFiles: [],
};

interface HomeProps {
    subForm?: SubFormVM;
}

export const Home = ({ subForm }: HomeProps) => {
    const [form, setForm] = React.useState<FormState>(emptyForm);
    const [removeAttachmentId, setRemoveAttachmentId] = React.useState(
        null as number | null
    );
    const [removeFedCertId, setRemoveFedCertId] = React.useState(
        null as number | null
    );
    const [storedW9, setStoredW9] = React.useState<FormAttachment | null>(null)
    const [storedEOC, setStoredEOC] = React.useState<FormAttachment | null>(null)
    const subFormStore = useSelector((s: ApplicationState) => s.subForm);

    const dispatch = useDispatch();

    // File input refs - used to get actual File objects at submission time
    const fileInputRefs = {
        w9Form: React.useRef<HTMLInputElement>(null),
        evidenceOfCoverage: React.useRef<HTMLInputElement>(null),
        sBEForm: React.useRef<HTMLInputElement>(null),
        mBEForm: React.useRef<HTMLInputElement>(null),
        wBEForm: React.useRef<HTMLInputElement>(null),
        vBEForm: React.useRef<HTMLInputElement>(null),
        uSBLNForm: React.useRef<HTMLInputElement>(null),
        hZBEForm: React.useRef<HTMLInputElement>(null),
        dBEForm: React.useRef<HTMLInputElement>(null),
        dVBEForm: React.useRef<HTMLInputElement>(null),
        lGBTBEForm: React.useRef<HTMLInputElement>(null),
    };

    // Helper to get file from ref
    const getFileFromRef = (ref: React.RefObject<HTMLInputElement>): File | null => {
        return ref.current?.files?.[0] ?? null;
    };

    // Centralized validation result (computed)
    const validationResult = React.useMemo<FormValidationResult>(() => {
        return validateForm(
            form,
            form.trades,
            subFormStore?.selectedState?.length ?? 0
        );
    }, [form, subFormStore?.selectedState?.length]);

    React.useEffect(() => {
        dispatch(SubFormState.actionCreators.getTradeList());
    }, []);

    React.useEffect(() => {
        if (subForm) {
            const newForm: FormState = {
                vendorName: subForm.vendorName,
                state: subForm.state,
                city: subForm.city,
                zip: subForm.primaryZip,
                billingAddress: subForm.billingAddress,
                country: subForm.country,

                p_state: subForm.physicalState,
                p_city: subForm.physicalCity,
                p_zip: subForm.physicalZip,
                p_Address: subForm.physicalAddress,
                p_country: subForm.physicalCountry,
                distributedEnergyMarket: subForm.distributedEnergyMarket,
                evMarket: subForm.evMarket,
                retailMarket: subForm.retailMarket,
                evitpCertified: subForm.evitpCertified,

                contactName: subForm.contactName,
                contactEmail: subForm.contactEmail,
                contactTitle: subForm.contactTitle,
                businessPhone: subForm.businessPhone,
                alternativePhone: subForm.altPhone,
                referral: subForm.referral,
                notes: subForm.notes,
                union: subForm.union,
                federalCertification: subForm.federalCertifications.map(
                    (x) => x.type.name
                ),
                heldLicenses: subForm.heldLicenses ?? [],
                vendorType: subForm.vendorType,
                passed: true,
                trades: subForm.trades.length,
                bparr1: '',
                bparr2: '',
                bparr3: '',
                zipOrexe: false,
                sameAddress: subForm.billingAddress == subForm.physicalAddress,

                yearsInBusiness: subForm.yearsInBusiness,
                numberOfEmployees: subForm.numberOfEmployees,
                modRate3Years: subForm.modRate3Years.map((x) =>
                    x ? x.toString() : '0'
                ),
                hasSafetyPolicy: subForm.hasSafetyPolicy,
                vendorId: subForm.vendorId,
                w9Form: subForm.attachments.find((x) => x.type == 'W9')?.fileName ?? '',
                evidenceOfCoverage: subForm.attachments.find((x) => x.type == 'EOC')?.fileName ?? '',
                sBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 1)?.attachment
                        .fileName ?? '',
                mBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 2)?.attachment
                        .fileName ?? '',
                wBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 3)?.attachment
                        .fileName ?? '',
                vBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 4)?.attachment
                        .fileName ?? '',
                uSBLNForm:
                    subForm.federalCertifications.find((x) => x.typeId == 5)?.attachment
                        .fileName ?? '',
                hZBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 6)?.attachment
                        .fileName ?? '',
                dBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 7)?.attachment
                        .fileName ?? '',
                dVBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 8)?.attachment
                        .fileName ?? '',
                lGBTBEForm:
                    subForm.federalCertifications.find((x) => x.typeId == 9)?.attachment
                        .fileName ?? '',
                attachments: subForm.attachments,
                isSubAcknowledged: subForm.isSubAcknowledged,
                certificateOfInsurance: subForm.certificateOfInsurance ?? '',
                bankingInfo: subForm.bankingInfo ?? '',
                website: subForm.website ?? '',
                licenseFiles: (subForm.heldLicenses ?? []).map(_ => null),
            };

            setForm(newForm);

            // Update the stored w9 if there is one
            const w9 = subForm.attachments.find(x => x.type === "W9");
            if (w9) {
                // We don't clear it out if null, so that if there was one at some point we preserve it until it is changed with another file
                setStoredW9(w9);
            }

            // Update the stored EOC if there is one
            const eoc = subForm.attachments.find(x => x.type === "EOC");
            if (eoc) {
                setStoredEOC(eoc);
            }
        }
    }, [subForm]);

    const updateFormInput = (e: React.FormEvent<HTMLInputElement>) => {
        let val: string | number | boolean;
        if (e.currentTarget.type === 'checkbox') {
            val = e.currentTarget.checked;
        } else if (e.currentTarget.type === 'number') {
            val = e.currentTarget.value === '' ? 0 : Number(e.currentTarget.value);
        } else {
            val = e.currentTarget.value;
        }
        setForm({
            ...form,
            [e.currentTarget.name]: val,
        } as any);
    };

    const updateFileFormInput = (e: React.FormEvent<HTMLInputElement>) => {
        let filename = '';

        if (e.currentTarget.files && e.currentTarget.files.length > 0) {
            filename = e.currentTarget.files[0].name;
        }

        setForm({
            ...form,
            [e.currentTarget.name]: filename,
        } as any);
    };

    const updateFormTextArea = (e: React.FormEvent<HTMLTextAreaElement>) => {
        let val = e.currentTarget.value;
        setForm({
            ...form,

            [e.currentTarget.name]: val,
        } as any);
    };

    const updateModYears = (e: React.FocusEvent<HTMLInputElement>) => {
        const idx = e.currentTarget.getAttribute('data-index');
        if (idx) {
            const i = parseInt(idx);
            const newRates = form.modRate3Years.slice().map((x, j) => {
                return i === j ? e.currentTarget.value : x;
            });
            setForm({
                ...form,
                modRate3Years: newRates,
            });
        }
    };

    const updateFormDropDown = (e: React.FormEvent<HTMLSelectElement>) => {
        let a = e.currentTarget.parentElement!.getElementsByTagName(
            'select'
        )[0] as HTMLSelectElement;
        let b = Array.prototype.slice.call(a.options) as Array<HTMLOptionElement>;
        const c = b.filter((x) => x.selected)[0].innerText;

        switch (e.currentTarget.name) {
            case 'vendorType':
            case 'federalCertification':
                if (c == '-- Please make a selection --') {
                    break;
                } else {
                    setForm({
                        ...form,

                        [e.currentTarget.name]: c,
                    } as any);
                }
                break;
            default:
                setForm({
                    ...form,

                    [e.currentTarget.name]: c,
                } as any);
                break;
        }
    };

    const saveSubmitButton = () => {
        // Use centralized validation
        if (!validationResult.isValid) {
            setForm({ ...form, passed: false });
            return;
        }

        const newForm: any = { ...form };

        // if the physical address is the same as the billing address
        if (form.sameAddress) {
            newForm.p_state = form.state.trim();
            newForm.p_city = form.city.trim();
            newForm.p_zip = form.zip;
            newForm.p_Address = form.billingAddress.trim();
            newForm.p_country = form.country.trim();
        } else {
            newForm.p_state = form.p_state.trim();
            newForm.p_city = form.p_city.trim();
            newForm.p_zip = form.p_zip;
            newForm.p_Address = form.p_Address.trim();
            newForm.p_country = form.p_country.trim();
        }

        // Collect files from refs instead of getElementById
        newForm.w9Form = getFileFromRef(fileInputRefs.w9Form);
        newForm.evidenceOfCoverage = getFileFromRef(fileInputRefs.evidenceOfCoverage);

        // Only collect certification files if they were selected
        if (form.sBEForm?.length > 0) {
            newForm.sBEForm = getFileFromRef(fileInputRefs.sBEForm);
        }
        if (form.mBEForm?.length > 0) {
            newForm.mBEForm = getFileFromRef(fileInputRefs.mBEForm);
        }
        if (form.wBEForm?.length > 0) {
            newForm.wBEForm = getFileFromRef(fileInputRefs.wBEForm);
        }
        if (form.vBEForm?.length > 0) {
            newForm.vBEForm = getFileFromRef(fileInputRefs.vBEForm);
        }
        if (form.uSBLNForm?.length > 0) {
            newForm.uSBLNForm = getFileFromRef(fileInputRefs.uSBLNForm);
        }
        if (form.hZBEForm?.length > 0) {
            newForm.hZBEForm = getFileFromRef(fileInputRefs.hZBEForm);
        }
        if (form.dBEForm?.length > 0) {
            newForm.dBEForm = getFileFromRef(fileInputRefs.dBEForm);
        }
        if (form.dVBEForm?.length > 0) {
            newForm.dVBEForm = getFileFromRef(fileInputRefs.dVBEForm);
        }
        if (form.lGBTBEForm?.length > 0) {
            newForm.lGBTBEForm = getFileFromRef(fileInputRefs.lGBTBEForm);
        }

        // Collect license files from state and filter out nulls
        newForm.licenseFiles = form.licenseFiles.filter((f): f is File => f !== null);

        setForm({ ...newForm, passed: true });
        dispatch(
            SubFormState.actionCreators.submitForm(
                newForm,
                subForm ? true : false
            )
        );
    };
    // to count the number of trades selected. In case the user added and removed couple times
    const updateSelectedIntoHome = (e: Trade[], plusMinus: number) => {
        plusMinus == 1
            ? setForm({ ...form, trades: form.trades + e.length })
            : setForm({ ...form, trades: form.trades - e.length });
    };

    const addHeldLicense = (value: HeldLicense, file: File | null = null) => {
        setForm({
            ...form,
            heldLicenses: [...form.heldLicenses, value],
            licenseFiles: [...form.licenseFiles, file]
        });
    }

    const removeHeldLicense = (idx: number) => {
        setForm({
            ...form,
            heldLicenses: form.heldLicenses.filter((x, i) => i !== idx),
            licenseFiles: form.licenseFiles.filter((x, i) => i !== idx)
        });
    }

    const updateHeldLicense = (idx: number, value: HeldLicense) => {
        // Always update state - validation happens at form submission
        setForm({ ...form, heldLicenses: form.heldLicenses.map((x, i) => i === idx ? value : x) });
    }

    const teleInput = (e: React.FormEvent<HTMLInputElement>) => {

        setForm({
            ...form,
            [e.currentTarget.name]: e.currentTarget.value,
        } as any);

    };

    const handleCheckbox = (e: React.FormEvent<HTMLInputElement>) => {
        const v = e.currentTarget.value;

        let a = form.federalCertification.filter((x) => x == v);

        if (a.length % 2 != 0) {
            const b = form.federalCertification.filter((x) => x != v);
            setForm({
                ...form,
                federalCertification: b,
            });
        } else {
            const joined = form.federalCertification.concat(v);

            setForm({
                ...form,
                federalCertification: joined,
            });
        }
    };

    const sameAddressCheckBox = () => {
        let checked = form.sameAddress;
        setForm({
            ...form,
            sameAddress: !checked,
        });
    };

    const listCountries = countriesShort.map((x, i) => (
        <option key={i}>{x}</option>
    ));

    const listStates = React.useMemo(() => {
        return states.map((x, i) => (
            <option
                value={x}
                //selected={x.toLowerCase() == subForm?.state.toLowerCase()}
                key={i}
            >
                {x}
            </option>
        ));
    }, [subForm]);
    const listStatesPhysical = React.useMemo(() => {
        return states.map((x, i) => (
            <option
                value={x}
                //selected={x.toLowerCase() == subForm?.physicalState.toLowerCase()}
                key={i}
            >
                {x}
            </option>
        ));
    }, [subForm]);

    // Derive validation flags from centralized validation result
    // These are used by the UI to conditionally apply 'is-invalid' classes
    const fieldErrors = validationResult.fieldErrors;
    const isFieldValid = (fieldName: string) => !(fieldName in fieldErrors);

    let vendorName = isFieldValid('vendorName'),
        vendorType = isFieldValid('vendorType'),
        city = isFieldValid('city'),
        state = isFieldValid('state'),
        zip = isFieldValid('zip'),
        billingAddress = isFieldValid('billingAddress'),
        p_city = isFieldValid('p_city'),
        p_state = isFieldValid('p_state'),
        p_zip = isFieldValid('p_zip'),
        p_Address = isFieldValid('p_Address'),
        contactName = isFieldValid('contactName'),
        contactEmail = isFieldValid('contactEmail'),
        businessPhone = isFieldValid('businessPhone'),
        union = isFieldValid('union'),
        w9Form = isFieldValid('w9Form'),
        evidenceOfCoverage = isFieldValid('evidenceOfCoverage'),
        sBEForm = isFieldValid('sBEForm'),
        mBEForm = isFieldValid('mBEForm'),
        wBEForm = isFieldValid('wBEForm'),
        vBEForm = isFieldValid('vBEForm'),
        uSBLNForm = isFieldValid('uSBLNForm'),
        hZBEForm = isFieldValid('hZBEForm'),
        dBEForm = isFieldValid('dBEForm'),
        dVBEForm = isFieldValid('dVBEForm'),
        lGBTBEForm = isFieldValid('lGBTBEForm'),
        yearsInBusiness = isFieldValid('yearsInBusiness'),
        numberOfEmployees = isFieldValid('numberOfEmployees'),
        generalDescription = isFieldValid('notes'),
        referral = isFieldValid('referral'),
        heldLicenses = isFieldValid('heldLicenses');

    const modalWarning = () => {
        return (
            <div className="container blackback">
                <div className="modal show" id="myModal" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div style={{ justifyContent: 'end' }} className="modal-header">
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-dismiss="modal"
                                    onClick={() => {
                                        dispatch(SubFormState.actionCreators.modalButton());
                                    }}
                                >
                                    &times;
                                </button>
                                <h4 className="modal-title gFont">Error: </h4>
                            </div>
                            <div className="modal-body gFont">
                                <p>A record for this subcontract exists.</p>
                                <br />
                                <p>
                                    Please contact a CoreStates representative for more
                                    information.
                                </p>
                                <p>Thank You</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    data-dismiss="modal"
                                    onClick={() => {
                                        dispatch(SubFormState.actionCreators.modalButton());
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const modal_Submitted = () => {
        return (
            <div className="container blackback">
                <div className="modal show" id="myModal" role="dialog">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div style={{ justifyContent: 'end' }} className="modal-header">
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-dismiss="modal"
                                    onClick={() => {
                                        dispatch(SubFormState.actionCreators.modalButton());
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body mb gFont">
                                <h1 className="bi bi-check-circle"></h1>
                                {subForm ? (
                                    <>
                                        <h1>Thank You</h1>
                                        <br />
                                        <h2>The form was saved successfully</h2>
                                    </>
                                ) : (
                                    <>
                                        <h1>Phase 1 Received</h1>
                                        <br />
                                        <h2>Your registration is not yet complete</h2>
                                        <p style={{ marginTop: '15px' }}>
                                            This is <b>Phase 1 of a 2-part registration</b>. After our team
                                            reviews your information, we'll email you a link to <b>Phase 2</b>.
                                            Your registration is not complete until the Phase 2 forms are
                                            submitted and reviewed.
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    data-dismiss="modal"
                                    onClick={() => {
                                        dispatch(SubFormState.actionCreators.modalButton());
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const _removeAttachment = () => {
        fetch(`api/SubForm/removeattachment?attachmentId=${removeAttachmentId}`, {
            method: 'DELETE',
        })
            .then((res) => Promise.all([res.ok, res.json()]))
            .then(([resOk, data]) => {
                if (resOk && subForm) {
                    dispatch(SubFormState.actionCreators.getVendorForm(subForm.vendorId));
                    setRemoveAttachmentId(null);
                }
            });
    };

    const _removeFedCert = () => {
        fetch(`api/SubForm/DeleteFedCert?id=${removeFedCertId}`, {
            method: 'DELETE',
        })
            .then((res) => Promise.all([res.ok, res.json()]))
            .then(([resOk, data]) => {
                if (resOk && subForm) {
                    dispatch(SubFormState.actionCreators.getVendorForm(subForm.vendorId));
                    setRemoveFedCertId(null);
                }
            });
    };

    const removeAttachmentComponent = removeAttachmentId ? (
        <Modal2 dialogClass="white">
            <div className="modal-body" style={{ textAlign: 'center' }}>
                Are you sure you want to delete this attachment?
            </div>
            <div className="modal-footer">
                <button className="btn btn-sm btn-danger" onClick={_removeAttachment}>
                    Delete
                </button>
                <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setRemoveAttachmentId(null)}
                >
                    Cancel
                </button>
            </div>
        </Modal2>
    ) : (
        []
    );

    const removeFedCertComponent = removeFedCertId ? (
        <Modal2 dialogClass="white">
            <div className="modal-body" style={{ textAlign: 'center' }}>
                Are you sure you want to delete this attachment?
            </div>
            <div className="modal-footer">
                <button className="btn btn-sm btn-danger" onClick={_removeFedCert}>
                    Delete
                </button>
                <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setRemoveFedCertId(null)}
                >
                    Cancel
                </button>
            </div>
        </Modal2>
    ) : (
        []
    );

    return (
        <div>
            {removeAttachmentComponent}
            {removeFedCertComponent}
            <div className="container">
                {/*<h1 className="gFont" style={{ marginLeft: '15px' }}><strong>New Vendor / Subcontractor</strong></h1>*/}
                <br />
                <small style={{ marginLeft: '0px' }}>
                    * indicates a required field
                </small>
                <F40FormRow>
                    <F40FormGroup noFlex width={'50%'}>
                        <div>
                            <b>Company Name*</b>
                        </div>
                        <input
                            className={`form-control ${vendorName || form.passed ? '' : 'is-invalid'
                                } `}
                            disabled={subForm ? true : false}
                            name="vendorName"
                            onBlur={updateFormInput}
                            defaultValue={subForm ? subForm.vendorName : ''}
                        />
                        {vendorName || form.passed ? (
                            ''
                        ) : (
                            <div className="invalid-feedback">
                                Please provide company name
                            </div>
                        )}
                    </F40FormGroup>
                    <F40FormGroup noFlex width={'25%'}>
                        <div>
                            <b>Vendor Type*</b>
                        </div>
                        <select
                            className={`form-select ${vendorType || form.passed ? '' : 'is-invalid'} `}
                            name="vendorType"
                            onChange={updateFormDropDown}
                            defaultValue={subForm ? subForm.vendorType : ''}
                        >
                            <option value="">-- Please make a selection --</option>
                            <option value="Consultant">Consultant</option>
                            <option value="Subcontractor">Subcontractor</option>
                            <option value="Material Supplier">Material Supplier</option>
                            <option value="Sub and Supplier">Sub and Supplier</option>
                        </select>
                        {vendorType || form.passed ? (
                            ''
                        ) : (
                            <div className="invalid-feedback">
                                Please provide vendor type
                            </div>
                        )}
                    </F40FormGroup>
                </F40FormRow>
                <F40FormRow>
                    <F40FormGroup noFlex width={'50%'}>
                        <div>
                            <b>Billing Address*</b>
                        </div>
                        <input
                            className={`form-control ${billingAddress || form.passed ? '' : 'is-invalid'
                                } `}
                            name="billingAddress"
                            onBlur={updateFormInput}
                            defaultValue={subForm ? subForm.billingAddress : ''}
                        />
                        {billingAddress || form.passed ? (
                            ''
                        ) : (
                            <div className="invalid-feedback">Please fill in an address</div>
                        )}
                    </F40FormGroup>
                    <F40FormGroup noFlex width={'25%'}>
                        <div>
                            <b>City*</b>
                        </div>
                        <input
                            className={`form-control ${city || form.passed ? '' : 'is-invalid'
                                } `}
                            name="city"
                            onBlur={updateFormInput}
                            defaultValue={subForm ? subForm.city : ''}
                        />
                        {city || form.passed ? (
                            ''
                        ) : (
                            <div className="invalid-feedback">Please provide a city</div>
                        )}
                    </F40FormGroup>
                    <F40FormGroup noFlex width={'25%'}>
                        <div>
                            <b>State/Province*</b>
                        </div>
                        <select
                            className={`form-select ${state || form.passed ? '' : 'is-invalid'
                                } `}
                            name="state"
                            placeholder="State"
                            defaultValue={subForm ? subForm.state : ''}
                            onBlur={updateFormDropDown}
                        >
                            <option></option>
                            {listStates}
                        </select>
                        {state || form.passed ? (
                            ''
                        ) : (
                            <div className="invalid-feedback">
                                Please select a state/province
                            </div>
                        )}
                    </F40FormGroup>
                </F40FormRow>
                <F40FormRow>
                    <F40FormGroup noFlex width={'50%'}>
                        <div>
                            <b>Country*</b>
                        </div>
                        <select
                            className="form-select"
                            name="country"
                            placeholder="Country"
                            onBlur={updateFormDropDown}
                            defaultValue={subForm ? subForm.country : ''}
                        >
                            {listCountries}
                        </select>
                    </F40FormGroup>
                    <F40FormGroup noFlex width={'25%'}>
                        <div>
                            <b>Zip{form.country === 'United States of America' && '*'}</b>
                        </div>
                        <input
                            className={`form-control ${zip || form.passed ? '' : 'is-invalid'
                                } `}
                            onBlur={updateFormInput}
                            name="zip"
                            maxLength={10}
                            defaultValue={subForm ? subForm.primaryZip : ''}
                        />
                        {zip || form.passed ? (
                            ''
                        ) : (
                            <div className="invalid-feedback">Please provide a zipcode</div>
                        )}
                    </F40FormGroup>
                </F40FormRow>
                <F40FormRow>
                    <F40FormGroup width={'600px'}>
                        {/*<input type="checkbox" onClick={sameAddressCheckBox} />*/}
                        <b>Physical Address is the same as Billing Address</b> &nbsp; &nbsp;
                        <SliderCheckbox
                            onChange={sameAddressCheckBox}
                            selected={
                                subForm
                                    ? subForm.billingAddress == subForm.physicalAddress
                                    : false
                            }
                            fieldName=""
                        />
                        &nbsp; &nbsp; &nbsp; &nbsp;
                    </F40FormGroup>
                </F40FormRow>
                {form.sameAddress ? (
                    ''
                ) : (
                    <>
                        <F40FormRow>
                            <F40FormGroup noFlex width={'50%'}>
                                <div>
                                    <b>Physical Address*</b>
                                </div>
                                <input
                                    className={`form-control ${p_Address || form.passed ? '' : 'is-invalid'
                                        } `}
                                    name="p_Address"
                                    onBlur={updateFormInput}
                                    defaultValue={subForm ? subForm.physicalAddress : ''}
                                />
                                {p_Address || form.passed ? (
                                    ''
                                ) : (
                                    <div className="invalid-feedback">
                                        Please fill in an address
                                    </div>
                                )}
                            </F40FormGroup>
                            <F40FormGroup noFlex width={'25%'}>
                                <div>
                                    <b>City*</b>
                                </div>
                                <input
                                    className={`form-control ${p_city || form.passed ? '' : 'is-invalid'
                                        } `}
                                    name="p_city"
                                    onBlur={updateFormInput}
                                    defaultValue={subForm ? subForm.physicalCity : ''}
                                />
                                {p_city || form.passed ? (
                                    ''
                                ) : (
                                    <div className="invalid-feedback">Please provide a city</div>
                                )}
                            </F40FormGroup>
                            <F40FormGroup noFlex width={'25%'}>
                                <div>
                                    <b>State/Province*</b>
                                </div>
                                <select
                                    className={`form-select ${p_state || form.passed ? '' : 'is-invalid'
                                        } `}
                                    name="p_state"
                                    placeholder="State"
                                    onBlur={updateFormDropDown}
                                    defaultValue={subForm ? subForm.physicalState : ''}
                                >
                                    <option></option>
                                    {listStatesPhysical}
                                </select>
                                {p_state || form.passed ? (
                                    ''
                                ) : (
                                    <div className="invalid-feedback">
                                        Please select a state/province
                                    </div>
                                )}
                            </F40FormGroup>
                        </F40FormRow>
                        <F40FormRow>
                            <F40FormGroup noFlex width={'50%'}>
                                <div>
                                    <b>Country</b>
                                </div>
                                <select
                                    className="form-select"
                                    name="p_country"
                                    placeholder="Country"
                                    onBlur={updateFormDropDown}
                                    defaultValue={subForm ? subForm.physicalCountry : ''}
                                >
                                    {listCountries}
                                </select>
                            </F40FormGroup>
                            <F40FormGroup noFlex width={'25%'}>
                                <div>
                                    <b>
                                        Zip
                                        {form.p_country === 'United States of America' && '*'}
                                    </b>
                                </div>
                                <input
                                    className={`form-control ${p_zip || form.passed ? '' : 'is-invalid'
                                        } `}
                                    onBlur={updateFormInput}
                                    name="p_zip"
                                    maxLength={10}
                                    defaultValue={subForm ? subForm.physicalZip : ''}
                                />

                                {p_zip || form.passed ? (
                                    ''
                                ) : (
                                    <div className="invalid-feedback">
                                        Please provide a zipcode
                                    </div>
                                )}
                            </F40FormGroup>
                        </F40FormRow>
                    </>
                )}
                {requiresTradesAndLicenses(form.vendorType) ? <>
                    <F40FormRow>
                        <F40FormGroup noFlex width={'50%'}>
                            <div>
                                <b>Market Sectors{requiresTradesAndLicenses(form.vendorType) && '*'} (select all that apply)</b>
                            </div>
                            <div style={{ height: '10px', width: '100%' }}></div>
                            <div style={{ display: 'flex' }}>
                                <b style={{ width: '130px' }}>Distributed Energy</b>
                                <SliderCheckbox
                                    onChange={updateFormInput}
                                    selected={subForm ? subForm.distributedEnergyMarket : false}
                                    fieldName="distributedEnergyMarket"
                                />
                                &nbsp; &nbsp; &nbsp; &nbsp;
                                <b>EV</b> &nbsp;
                                <SliderCheckbox
                                    onChange={updateFormInput}
                                    selected={subForm ? subForm.evMarket : false}
                                    fieldName="evMarket"
                                />
                                &nbsp; &nbsp; &nbsp; &nbsp;
                                <b>Retail/Commercial</b> &nbsp;
                                <SliderCheckbox
                                    onChange={updateFormInput}
                                    selected={subForm ? subForm.retailMarket : false}
                                    fieldName="retailMarket"
                                />
                                &nbsp;
                            </div>
                        </F40FormGroup>
                    </F40FormRow>
                    <F40FormRow>
                        <F40FormGroup noFlex width={'100%'}>
                            <div style={{ display: 'flex' }}>
                                <b style={{ width: '130px' }}>EVITP Certified</b>
                                <SliderCheckbox
                                    onChange={updateFormInput}
                                    selected={subForm ? subForm.evitpCertified : false}
                                    fieldName="evitpCertified"
                                />
                            </div>
                        </F40FormGroup>
                    </F40FormRow>

                    <F40FormRow>
                        <F40FormGroup noFlex width={'50%'}>
                            <TradeListSelection
                                allTradeList={subFormStore.trade}
                                selectedList={subFormStore.selectedTrade}
                                updateList={(selected) => {
                                    dispatch(
                                        SubFormState.actionCreators.selectedTradeList(selected)
                                    );
                                }}
                                removeSelectedTradeList={(selected) => {
                                    dispatch(
                                        SubFormState.actionCreators.removeSelectedTradeList(selected)
                                    );
                                }}
                                selectedTrades={updateSelectedIntoHome}
                                isInvalid={!(form.trades > 0 || form.passed)}
                            />
                        </F40FormGroup>
                        <F40FormGroup noFlex width={'50%'}>
                            <ServiceAreas
                                selectedStatesList={(selected) =>
                                    dispatch(
                                        SubFormState.actionCreators.selectedStatesList(selected)
                                    )
                                }
                                selectedStates={subFormStore.selectedState}
                                isInvalid={!(subFormStore.selectedState.length > 0 || form.passed)}
                            />
                        </F40FormGroup>
                    </F40FormRow>
                </> : []}
                {requiresTradesAndLicenses(form?.vendorType ?? '') && <div>
                    <F40FormRow>
                        <HeldLicensesGrid
                            form={form}
                            setForm={setForm}
                            addLicense={addHeldLicense}
                            removeLicense={removeHeldLicense}
                            updateLicense={updateHeldLicense}
                            fieldErrors={fieldErrors}
                        />
                    </F40FormRow>
                </div>}
                <div>
                    <F40FormRow>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Contact Name*</b>
                            </div>
                            <input
                                className={`form-control ${contactName || form.passed ? '' : 'is-invalid'
                                    } `}
                                name="contactName"
                                onBlur={updateFormInput}
                                defaultValue={subForm ? subForm.contactName : ''}
                            />

                            {contactName || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please provide a contact name
                                </div>
                            )}
                        </F40FormGroup>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Contact Email*</b>
                            </div>
                            <input
                                className={`form-control ${contactEmail || form.passed ? '' : 'is-invalid'
                                    } `}
                                name="contactEmail"
                                onBlur={updateFormInput}
                                placeholder="example@mydomain.com"
                                defaultValue={subForm ? subForm.contactEmail : ''}
                            />
                            <small>All links and correspondence for registration purposes will be sent to this email address</small>
                            {contactEmail || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please provide a contact email
                                </div>
                            )}
                        </F40FormGroup>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Contact Title</b>
                            </div>
                            <input
                                className="form-control"
                                name="contactTitle"
                                onBlur={updateFormInput}
                                defaultValue={subForm ? subForm.contactTitle : ''}
                            />
                        </F40FormGroup>
                    </F40FormRow>
                    <F40FormRow>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Business Phone*</b>
                            </div>
                            <input
                                className={`form-control ${businessPhone || form.passed ? '' : 'is-invalid'
                                    } `}
                                key={1}
                                name="businessPhone"
                                defaultValue={subForm ? subForm.businessPhone : ''}
                                onChange={teleInput}
                            />
                            {businessPhone || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please fill in a phone number
                                </div>
                            )}
                        </F40FormGroup>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Alternative / Mobile Phone</b>
                            </div>
                            <input
                                className="form-control"
                                key={2}
                                name="alternativePhone"
                                onChange={teleInput}
                                //value={form.alternativePhone}
                                defaultValue={subForm ? subForm.altPhone : ''}
                            />
                        </F40FormGroup>
                    </F40FormRow>
                    <F40FormRow style={{ display: 'flex' }}>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Years In Business*</b>
                            </div>
                            <input
                                className={`form-control ${yearsInBusiness || form.passed ? '' : 'is-invalid'
                                    } `}
                                type="number"
                                onChange={updateFormInput}
                                name="yearsInBusiness"
                                defaultValue={subForm ? subForm.yearsInBusiness.toString() : ''}
                            />
                            {yearsInBusiness || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please fill in valid year amount
                                </div>


                            )}

                            <div style={{ marginTop: '16px' }}>
                                <b>Website</b>
                            </div>
                            <input
                                className="form-control"
                                name="website"
                                onBlur={updateFormInput}
                                defaultValue={subForm ? subForm.website : ''}
                            />

                        </F40FormGroup>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Number of Employees*</b>
                            </div>
                            <input
                                className={`form-control ${numberOfEmployees || form.passed ? '' : 'is-invalid'} `}
                                style={{ width: '50%' }}
                                type="number"
                                onChange={updateFormInput}
                                name="numberOfEmployees"
                                defaultValue={
                                    subForm ? subForm.numberOfEmployees.toString() : ''
                                }
                            />
                            {numberOfEmployees || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please fill in valid number
                                </div>
                            )}
                        </F40FormGroup>
                        <F40FormGroup
                            noFlex
                            width={'33%'}
                            style={{
                                position: 'relative',
                                paddingRight: '0px',
                                display: 'block',
                            }}
                        >
                            <div>
                                <b>Mod Rate Last 3 Years</b>
                            </div>
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    marginBottom: '0.5em',
                                }}
                            >
                                <div
                                    style={{ position: 'absolute', left: '-35px', top: '3px' }}
                                >{`${new Date().getFullYear() - 1}`}</div>
                                <input
                                    className="form-control"
                                    type="number"
                                    max="2.0"
                                    min="0.00"
                                    data-index="0"
                                    onBlur={updateModYears}
                                    defaultValue={
                                        subForm ? subForm.modRate3Years[0]?.toString() : ''
                                    }
                                />
                            </div>
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    marginBottom: '0.5em',
                                }}
                            >
                                <span
                                    style={{ position: 'absolute', left: '-35px', top: '3px' }}
                                >{`${new Date().getFullYear() - 2}`}</span>
                                <input
                                    className="form-control"
                                    type="number"
                                    max="2.0"
                                    min="0.00"
                                    data-index="1"
                                    onBlur={updateModYears}
                                    defaultValue={
                                        subForm ? subForm.modRate3Years[1]?.toString() : ''
                                    }
                                />
                            </div>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <span
                                    style={{ position: 'absolute', left: '-35px', top: '3px' }}
                                >{`${new Date().getFullYear() - 3}`}</span>
                                <input
                                    className="form-control"
                                    type="number"
                                    max="2.0"
                                    min="0.00"
                                    data-index="2"
                                    onBlur={updateModYears}
                                    defaultValue={
                                        subForm ? subForm.modRate3Years[2]?.toString() : ''
                                    }
                                />
                            </div>
                        </F40FormGroup>
                    </F40FormRow>
                    <F40FormRow>
                        <F40FormGroup noFlex width={'25%'}>
                            <div>
                                <b>Do you have a written Safety Policy?</b>
                            </div>
                            <SliderCheckbox
                                onChange={updateFormInput}
                                selected={subForm ? subForm.hasSafetyPolicy : false}
                                fieldName="hasSafetyPolicy"
                            />
                        </F40FormGroup>
                    </F40FormRow>
                </div>

                <div className="">
                    <F40FormRow>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>Labor Status*</b>
                            </div>
                            <select
                                className={`form-select ${union || form.passed ? '' : 'is-invalid'
                                    } `}
                                name="union"
                                onBlur={updateFormDropDown}
                                defaultValue={subForm ? subForm.union : ''}
                            >
                                <option value="">-- Please make a selection --</option>
                                <option value="Open Shop">Open Shop</option>
                                <option value="Merit">Merit</option>
                                <option value="Non-Union">Non-Union</option>
                                <option value="Union">Union</option>
                            </select>

                            {union || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please select labor status
                                </div>
                            )}
                        </F40FormGroup>
                        <F40FormGroup noFlex width={'33%'}>
                            <div>
                                <b>General Description of Services*</b>
                            </div>
                            <textarea
                                className={`form-control ${generalDescription || form.passed ? '' : 'is-invalid'
                                    } `}
                                name="notes"
                                onBlur={updateFormTextArea}
                                defaultValue={subForm ? subForm.notes : ''}
                                rows={3}
                            />
                            {generalDescription || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please provide a description
                                </div>
                            )}
                        </F40FormGroup>
                    </F40FormRow>
                    <F40FormRow>
                        <F40FormGroup noFlex width="55%">
                            <b style={{ width: '350px' }}>Please upload your W9*</b>
                            {form.attachments.find((x) => x.type === 'W9') ? (
                                <>
                                    <p>
                                        <a
                                            href={form.attachments.find((x) => x.type == 'W9')?.filePath}
                                            target="_blank"
                                        >
                                            {form.attachments.find((x) => x.type == 'W9')?.fileName}{' '}
                                        </a>
                                        <br />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            style={{ marginTop: '5px' }}
                                            onClick={() => {
                                                const updatedAttachments = form.attachments.filter(
                                                    (x) => x.type !== 'W9'
                                                );
                                                setForm({ ...form, attachments: updatedAttachments });
                                            }}
                                        >
                                            Replace
                                        </button>

                                    </p>
                                </>
                            ) : (
                                <>
                                    <input
                                        ref={fileInputRefs.w9Form}
                                        className={`form-control ${w9Form || form.passed ? '' : 'is-invalid'}`}
                                        type="file"
                                        id="w9Form"
                                        name="w9Form"
                                        accept=".pdf"
                                        onChange={updateFileFormInput}
                                    />
                                    {storedW9 !== null &&
                                        <div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                style={{ marginTop: '5px' }}
                                                onClick={() => {
                                                    const updatedAttachments = form.attachments.concat([storedW9]);
                                                    setForm({ ...form, attachments: updatedAttachments });
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    }
                                    {w9Form || form.passed ? (
                                        ''
                                    ) : (
                                        <div className="invalid-feedback">Please submit your W9 form</div>
                                    )}
                                </>
                            )}
                        </F40FormGroup>

                    </F40FormRow>
                    <F40FormRow>
                        <F40FormGroup noFlex width="55%">
                            <b style={{ width: '350px' }}>Please upload your Evidence of Coverage / Proof of Insurance*</b>
                            {form.attachments.find((x) => x.type === 'EOC') ? (
                                <>
                                    <p>
                                        <a
                                            href={form.attachments.find((x) => x.type == 'EOC')?.filePath}
                                            target="_blank"
                                        >
                                            {form.attachments.find((x) => x.type == 'EOC')?.fileName}{' '}
                                        </a>
                                        <br />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            style={{ marginTop: '5px' }}
                                            onClick={() => {
                                                const updatedAttachments = form.attachments.filter(
                                                    (x) => x.type !== 'EOC'
                                                );
                                                setForm({ ...form, attachments: updatedAttachments });
                                            }}
                                        >
                                            Replace
                                        </button>

                                    </p>
                                </>
                            ) : (
                                <>
                                    <input
                                        ref={fileInputRefs.evidenceOfCoverage}
                                        className={`form-control ${evidenceOfCoverage || form.passed ? '' : 'is-invalid'}`}
                                        type="file"
                                        id="evidenceOfCoverage"
                                        name="evidenceOfCoverage"
                                        accept=".pdf"
                                        onChange={updateFileFormInput}
                                    />
                                    {storedEOC !== null &&
                                        <div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                style={{ marginTop: '5px' }}
                                                onClick={() => {
                                                    const updatedAttachments = form.attachments.concat([storedEOC]);
                                                    setForm({ ...form, attachments: updatedAttachments });
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    }
                                    {evidenceOfCoverage || form.passed ? (
                                        ''
                                    ) : (
                                        <div className="invalid-feedback">Please submit your Evidence of Coverage / Proof of Insurance</div>
                                    )}
                                </>
                            )}
                        </F40FormGroup>

                    </F40FormRow>
                    <F40FormRow>
                        <div>
                            <div style={{ width: '825px' }}>
                                <strong>Federal Certification (select all that apply)</strong>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        name="hello"
                                        value="Small Business Enterprise (SBE)"
                                        disabled={form.attachments.some((x) => x.type == 'SBE')}
                                        checked={form.federalCertification.includes(
                                            'Small Business Enterprise (SBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        Small Business Enterprise (SBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 1) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 1
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 1
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 1
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'Small Business Enterprise (SBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.sBEForm}
                                                    className={sBEForm || form.passed ? '' : 'is-invalid'}
                                                    type="file"
                                                    id="sBEForm"
                                                    name="sBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {sBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="Minority Owned Business Enterprise (MBE)"
                                        disabled={form.attachments.some((x) => x.type == 'MBE')}
                                        checked={form.federalCertification.includes(
                                            'Minority Owned Business Enterprise (MBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        Minority Owned Business Enterprise (MBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 2) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 2
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 2
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 2
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'Minority Owned Business Enterprise (MBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.mBEForm}
                                                    className={mBEForm || form.passed ? '' : 'is-invalid'}
                                                    type="file"
                                                    id="mBEForm"
                                                    name="mBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {mBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="Woman Owned Business Enterprise (WBE)"
                                        disabled={form.attachments.some((x) => x.type == 'WBE')}
                                        checked={form.federalCertification.includes(
                                            'Woman Owned Business Enterprise (WBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        Woman Owned Business Enterprise (WBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 3) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 3
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 3
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 3
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'Woman Owned Business Enterprise (WBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.wBEForm}
                                                    className={wBEForm || form.passed ? '' : 'is-invalid'}
                                                    type="file"
                                                    id="wBEForm"
                                                    name="wBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {wBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="Veteran Owned Business Enterprise (VBE)"
                                        disabled={form.attachments.some((x) => x.type == 'VBE')}
                                        checked={form.federalCertification.includes(
                                            'Veteran Owned Business Enterprise (VBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        Veteran Owned Business Enterprise (VBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 4) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 4
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 4
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 4
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'Veteran Owned Business Enterprise (VBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.vBEForm}
                                                    className={vBEForm || form.passed ? '' : 'is-invalid'}
                                                    type="file"
                                                    id="vBEForm"
                                                    name="vBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {vBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="People with Disabilities (USBLN)"
                                        disabled={form.attachments.some((x) => x.type == 'USBLN')}
                                        checked={form.federalCertification.includes(
                                            'People with Disabilities (USBLN)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        People with Disabilities (USBLN)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 5) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 5
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 5
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 5
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'People with Disabilities (USBLN)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.uSBLNForm}
                                                    className={
                                                        uSBLNForm || form.passed ? '' : 'is-invalid'
                                                    }
                                                    type="file"
                                                    id="uSBLNForm"
                                                    name="uSBLNForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {uSBLNForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="HUB Zone Business Enterprise (HZBE)"
                                        disabled={form.attachments.some((x) => x.type == 'HZBE')}
                                        checked={form.federalCertification.includes(
                                            'HUB Zone Business Enterprise (HZBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        HUB Zone Business Enterprise (HZBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 6) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 6
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 6
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 6
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'HUB Zone Business Enterprise (HZBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.hZBEForm}
                                                    className={
                                                        hZBEForm || form.passed ? '' : 'is-invalid'
                                                    }
                                                    type="file"
                                                    id="hZBEForm"
                                                    name="hZBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {hZBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="Small Disadvantaged Business Enterprise (DBE)"
                                        disabled={form.attachments.some((x) => x.type == 'DBE')}
                                        checked={form.federalCertification.includes(
                                            'Small Disadvantaged Business Enterprise (DBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        Small Disadvantaged Business Enterprise (DBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 7) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 7
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 7
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 7
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'Small Disadvantaged Business Enterprise (DBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.dBEForm}
                                                    className={dBEForm || form.passed ? '' : 'is-invalid'}
                                                    type="file"
                                                    id="dBEForm"
                                                    name="dBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {dBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="Service-Disabled Veteran Owned Business Enterprise (DVBE)"
                                        disabled={form.attachments.some((x) => x.type == 'DVBE')}
                                        checked={form.federalCertification.includes(
                                            'Service-Disabled Veteran Owned Business Enterprise (DVBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        Service-Disabled Veteran Owned Business Enterprise (DVBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 8) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 8
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 8
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 8
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'Service-Disabled Veteran Owned Business Enterprise (DVBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.dVBEForm}
                                                    className={
                                                        dVBEForm || form.passed ? '' : 'is-invalid'
                                                    }
                                                    type="file"
                                                    id="dVBEForm"
                                                    name="dVBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {dVBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>
                                <br />
                                <F40FormGroup
                                    style={{ display: 'inline-flex', alignItems: 'baseline' }}
                                    width="100%"
                                >
                                    <input
                                        onChange={(e) => handleCheckbox(e)}
                                        type="checkbox"
                                        value="Lesbian, Gay, Bisexual and/or Transgender Owned Business Enterprise (LGBTBE)"
                                        disabled={form.attachments.some((x) => x.type == 'LGBTBE')}
                                        checked={form.federalCertification.includes(
                                            'Lesbian, Gay, Bisexual and/or Transgender Owned Business Enterprise (LGBTBE)'
                                        )}
                                    />
                                    <label className="cert-label">
                                        Lesbian, Gay, Bisexual and/or Transgender Owned Business
                                        Enterprise (LGBTBE)
                                    </label>
                                    {subForm?.federalCertifications.some((x) => x.typeId == 9) ? (
                                        <div style={{ width: '100%' }}>
                                            <a
                                                style={{ marginRight: '8px' }}
                                                href={
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 9
                                                    )?.attachment.filePath
                                                }
                                                target="_blank"
                                            >
                                                {
                                                    subForm.federalCertifications.find(
                                                        (x) => x.typeId == 9
                                                    )?.attachment.fileName
                                                }
                                            </a>
                                            <i
                                                className="bi bi-trash"
                                                onClick={() =>
                                                    setRemoveFedCertId(
                                                        subForm.federalCertifications.find(
                                                            (x) => x.typeId == 9
                                                        )?.id ?? null
                                                    )
                                                }
                                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                                title="Remove attachment"
                                            ></i>
                                        </div>
                                    ) : (
                                        form.federalCertification.includes(
                                            'Lesbian, Gay, Bisexual and/or Transgender Owned Business Enterprise (LGBTBE)'
                                        ) && (
                                            <div>
                                                <input
                                                    ref={fileInputRefs.lGBTBEForm}
                                                    className={
                                                        lGBTBEForm || form.passed ? '' : 'is-invalid'
                                                    }
                                                    type="file"
                                                    id="lGBTBEForm"
                                                    name="lGBTBEForm"
                                                    accept=".pdf"
                                                    onChange={updateFileFormInput}
                                                />
                                                {lGBTBEForm || form.passed ? (
                                                    ''
                                                ) : (
                                                    <div className="invalid-feedback">
                                                        Please submit valid certification
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </F40FormGroup>

                                <br />
                            </div>
                        </div>
                    </F40FormRow>
                    <F40FormRow>
                        <F40FormGroup noFlex width={'50%'}>
                            <div>
                                <b>Tell Us How You Found Us*</b>
                            </div>
                            <select
                                className={`form-select ${form.referral || form.passed ? '' : 'is-invalid'} `}
                                name="referral"
                                onChange={updateFormDropDown}
                                defaultValue={subForm ? subForm.referral : ''}
                            >
                                <option value="">-- Please make a selection --</option>
                                <option value="LinkedIn">LinkedIn</option>
                                <option value="Estimating request">Estimating request</option>
                                <option value="Word of mouth/ referral">Word of mouth/ referral</option>
                                <option value="Search engine">Search engine</option>
                                <option value="Social Media">Social Media</option>
                                <option value="Other">Other</option>
                            </select>
                            {form.referral || form.passed ? (
                                ''
                            ) : (
                                <div className="invalid-feedback">
                                    Please select an option
                                </div>
                            )}
                        </F40FormGroup>
                    </F40FormRow>



                </div>
                <div
                    style={{
                        marginTop: '8px',
                        marginBottom: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {requiresSubAcknowledgment(form?.vendorType ?? '') ? (
                        <div>
                            <br />
                            <input
                                onChange={updateFormInput}
                                defaultChecked={form.isSubAcknowledged}
                                type="checkbox"
                                id="acknowledgement"
                                name="isSubAcknowledged"
                            />
                            <label htmlFor="acknowledgement">
                                By submitting this form, I agree to the acknowledgements
                                outlined in the{' '}
                                <a target="_blank" href="/pdf/Partner Acknowledgement Form.pdf">
                                    Partner Acknowledgement Form
                                </a>
                            </label>
                            <br />
                        </div>
                    ) : null}
                </div>
                <div className="panel panel-default">
                    <div
                        className=""
                        style={{
                            display: 'flex',
                            padding: '20px 10px',
                        }}
                    >
                        <div className="col-sm-2" style={{ width: '100px' }}>
                            <button
                                className="btn btn-dark-blue submitButton"
                                onClick={saveSubmitButton}
                                disabled={
                                    requiresSubAcknowledgment(form.vendorType) && !form.isSubAcknowledged
                                }
                            >
                                {subForm ? 'Save' : 'Submit'}
                            </button>
                        </div>

                        <div
                            style={{
                                position: 'relative',
                                minHeight: '1px',
                                paddingRight: '15px',
                                paddingLeft: '15px',
                            }}
                            className="col-sm-11"
                        >
                            Thank you for your interest in becoming a partner with us. Someone on our staff will reach out to you
                            shortly to review your application. If accepted, additional
                            information may be required. Questions can be sent to
                            procurement@core-states.com.
                        </div>
                    </div>
                    {/*<div className="col-sm-4 manual">*/}
                    {/*  {form.vendorType != "Material Supplier Only" ? PdfDownloaderButton("ConstructionEmployeeSafetyManual") : ""}*/}
                    {/*</div>*/}
                </div>

                {subFormStore.message ? modalWarning() : ''}
                {subFormStore.success ? modal_Submitted() : ''}
            </div>
        </div>
    );
};

const HeldLicensesGrid = (props: {
    form: FormState,
    setForm: React.Dispatch<React.SetStateAction<FormState>>,
    addLicense: (license: HeldLicense, file: File | null) => void,
    removeLicense: (id: number) => void,
    updateLicense: (idx: number, license: HeldLicense) => void,
    fieldErrors: Record<string, string>,
}) => {
    const { form, setForm, addLicense, removeLicense, updateLicense, fieldErrors } = props;
    const [showAddLine, setShowAddLine] = React.useState(false);
    const [newRow, setNewRow] = React.useState<HeldLicense>({
        id: 0,
        partnerRegistrationDetailId: form.vendorId,
        licenseType: '',
        licenseNumber: '',
        state: '',
        fileName: '',
        filePath: '',
    });
    const [newRowFile, setNewRowFile] = React.useState<File | null>(null);
    const [newRowValid, setNewRowValid] = React.useState(false);

    React.useEffect(() => {
        if (newRow.licenseNumber.length > 0 && newRow.licenseType.length > 0 && newRowFile !== null) {
            setNewRowValid(true);
        } else {
            setNewRowValid(false);
        }
    }, [newRow, newRowFile]);

    const _addLicense = (license: HeldLicense) => {
        // Pass the file along with the license to avoid race condition
        addLicense(license, newRowFile);
        setShowAddLine(false);

        // Reset the new row state
        setNewRow({
            id: 0,
            partnerRegistrationDetailId: form.vendorId,
            licenseType: '',
            licenseNumber: '',
            state: '',
            fileName: '',
            filePath: '',
        });
        setNewRowFile(null);
    };

    const handleFileUpload = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const license = form.heldLicenses[idx];

            // Update the license fileName
            updateLicense(idx, { ...license, fileName: file.name });

            // Update the licenseFiles array at the corresponding index
            const newLicenseFiles = [...form.licenseFiles];
            newLicenseFiles[idx] = file;

            setForm({
                ...form,
                licenseFiles: newLicenseFiles
            });
        }
    };

    const handleNewRowFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setNewRowFile(file);
            setNewRow({ ...newRow, fileName: file.name });
        }
    };

    // Check for license-related validation errors
    const hasLicenseError = !form.passed && (fieldErrors.heldLicenses || fieldErrors.licenseFiles);
    const licenseErrorMessage = fieldErrors.heldLicenses || fieldErrors.licenseFiles;

    return (
        <div style={{ border: hasLicenseError ? '1px solid red' : '1px solid lightgray', borderRadius: '5px', padding: '15px 20px', width: '100%', marginBottom: '10px' }}>
            <h5>Held Licenses</h5>
            {hasLicenseError && (
                <div className="text-danger mb-2" style={{ fontSize: '0.875rem' }}>
                    {licenseErrorMessage}
                </div>
            )}
            <div className={`cust-table ${hasLicenseError ? 'is-invalid' : ''}`}>
                <F40FormRow className='table-header' style={{ height: '1.5em', paddingLeft: '0px' }}>
                    <F40FormGroup width='20%'><b>License Type*</b></F40FormGroup>
                    <F40FormGroup width='20%'><b>License Number*</b></F40FormGroup>
                    <F40FormGroup width='20%'><b>State</b></F40FormGroup>
                    <F40FormGroup width='30%'><b>Upload File*</b></F40FormGroup>
                    <F40FormGroup width='10%'></F40FormGroup>
                </F40FormRow>
                <hr />
                <div>
                    {form.heldLicenses.map((license, idx) => (
                        <F40FormRow className='' key={idx} style={{ height: '2.5em', paddingLeft: '0px' }}>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline' }} width="20%">
                                <input
                                    className='form-control'
                                    value={license.licenseType}
                                    onChange={(e) => updateLicense(idx, { ...license, licenseType: e.target.value })}
                                />
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline' }} width="20%">
                                <input
                                    className='form-control'
                                    value={license.licenseNumber}
                                    onChange={(e) => updateLicense(idx, { ...license, licenseNumber: e.target.value })}
                                />
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline' }} width="20%">
                                <select
                                    className={`form-select`}
                                    onChange={(e) => updateLicense(idx, { ...license, state: e.currentTarget.value })}
                                    value={license.state}
                                >
                                    <option></option>
                                    {states.map(x => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} width="30%">
                                {(license.fileName || form.licenseFiles[idx]) ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span className="bi bi-file-earmark-pdf" style={{ color: '#dc3545' }}></span>
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                                            {form.licenseFiles[idx]?.name || license.fileName}
                                        </span>
                                        <label
                                            htmlFor={`license_file_${idx}`}
                                            className="btn btn-sm btn-outline-secondary"
                                            style={{ cursor: 'pointer', marginBottom: 0, lineHeight: 1.25 }}
                                        >
                                            Change
                                        </label>
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            id={`license_file_${idx}`}
                                            onChange={(e) => handleFileUpload(idx, e)}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                ) : (
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        className={`form-control ${form.passed ? '' : 'is-invalid'}`}
                                        id={`license_file_${idx}`}
                                        onChange={(e) => handleFileUpload(idx, e)}
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                )}
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline' }} width="10%">
                                <button
                                    className='btn btn-sm btn-danger bi bi-trash'
                                    onClick={() => removeLicense(idx)}
                                />
                            </F40FormGroup>
                        </F40FormRow>
                    ))}

                    {showAddLine ?
                        <F40FormRow className='' style={{ height: '2.5em', paddingLeft: '0px' }}>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline' }} width="20%">
                                <input
                                    className='form-control'
                                    value={newRow.licenseType}
                                    onChange={(e) => setNewRow({ ...newRow, licenseType: e.target.value })}
                                    placeholder={'e.g. Electrical, GC'}
                                />
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline' }} width="20%">
                                <input
                                    className='form-control'
                                    value={newRow.licenseNumber}
                                    onChange={(e) => setNewRow({ ...newRow, licenseNumber: e.target.value })}
                                />
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline' }} width="20%">
                                <select
                                    className={`form-select`}
                                    name="p_state"
                                    placeholder="State"
                                    onChange={(e) => setNewRow({ ...newRow, state: e.currentTarget.value })}
                                    value={newRow.state}
                                >
                                    <option></option>
                                    {states.map(x => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'flex', flexDirection: 'column', gap: '4px' }} width="30%">
                                <input
                                    type="file"
                                    accept=".pdf"
                                    className={`form-control ${newRowFile !== null ? '' : 'is-invalid'}`}
                                    onChange={handleNewRowFileUpload}
                                    style={{ fontSize: '0.875rem' }}
                                />
                                {newRow.fileName && <small style={{ display: 'block', marginTop: '2px' }}>{newRow.fileName}</small>}
                            </F40FormGroup>
                            <F40FormGroup style={{ display: 'inline-flex', alignItems: 'baseline', gap: '4px' }} width="10%">
                                <button
                                    disabled={!newRowValid}
                                    className='btn btn-sm btn-primary'
                                    onClick={() => _addLicense(newRow)}
                                >
                                    Save
                                </button>
                                <button
                                    className='btn btn-sm btn-outline-secondary'
                                    onClick={() => {
                                        setShowAddLine(false);
                                        setNewRow({
                                            id: 0,
                                            partnerRegistrationDetailId: form.vendorId,
                                            licenseType: '',
                                            licenseNumber: '',
                                            state: '',
                                            fileName: '',
                                            filePath: '',
                                        });
                                        setNewRowFile(null);
                                    }}
                                    title="Cancel"
                                >
                                    <span className="">Cancel</span>
                                </button>
                            </F40FormGroup>
                        </F40FormRow>
                        :
                        <F40FormRow style={{ paddingLeft: '0px' }}>
                            <button className='btn btn-sm btn-primary' style={{ marginTop: '20px' }} onClick={() => setShowAddLine(true)}>
                                Add License Row
                            </button>
                        </F40FormRow>
                    }
                </div>
            </div>
            {form.heldLicenses.length > 0 || form.passed ? (
                ''
            ) : (
                <div className="invalid-feedback">
                    Please provide a held license
                </div>
            )}
        </div>
    );
}
